/**
 * R6 Ist-Versteuerung (§ 13 Abs. 1 Nr. 1b, § 20 UStG): je Zahlung eine Zeile mit signiertem Betrag
 * im Zuflussmonat; interne Verrechnungs-Legs (Kautionseinbehalt, Guthaben-Verrechnung) sind
 * Vereinnahmung → VERRECHNUNG, das Kautions-Leg selbst ist kein Entgelt (ausgeschlossen).
 * Unter Soll-Besteuerung erzeugt eine Zahlung keine Zeile (Rücklastschrift nur INFO).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {createIssue} from '../issues';
import {buildLine, setupDeepLink} from '../line';
import {monthKey, monthLabel, monthStart, resolveLeistungsmonat} from '../month';
import {isTaxableCategory} from '../resolve-vat-category';
import {round2, signedAmount} from '../rounding';
import {BookingLite, EMPTY_RULE_OUTPUT, PaymentLite, RuleContext, RuleOutput, VatIssue} from '../types';
import {buildExcludedLine, classifyBooking, classifyPayment, issueIDsOf, linkIssues} from './exclusions';
import {classifyBookingForSoll, classifyPaymentForIst} from './profile-wechsel';
import {artLabel, openGross} from './soll';

export const buildIstLines = (ctx: RuleContext, payments: PaymentLite[], bookings: BookingLite[]): RuleOutput => {
    const out = EMPTY_RULE_OUTPUT();

    for (const payment of payments) {
        const c = classifyPayment(ctx, payment);
        const booking = payment.bookingID ? ctx.bookingsByID.get(payment.bookingID) : undefined;
        const art = payment.art ?? booking?.art ?? null;
        const gross = signedAmount(payment.amount, payment.type);
        const paymentKey = payment.date ? monthKey(payment.date) : monthKey(ctx.range.start);
        const lm = booking ? resolveLeistungsmonat(booking) : null;
        const baseLabel = `Zufluss ${artLabel(art)}${lm ? ` ${monthLabel(lm.monthKey)}` : ''}`;

        if (c.excluded) {
            const line = buildExcludedLine(c, paymentKey, 'PAYMENT_DATE', gross, baseLabel);
            out.lines.push(line);
            out.issues.push(...linkIssues(c.issues, line.id));
            continue;
        }

        // payment.date ist hier gesetzt – sonst wäre die Quelle als DATE_MISSING ausgeschlossen
        const treatment = classifyPaymentForIst(ctx.profileAt, lm ? monthStart(lm.monthKey) : null, payment.date);
        const issues: VatIssue[] = [...c.issues];

        if (treatment === 'SOLL_REGIME') {
            if (gross < 0) {
                out.issues.push(createIssue('RUECKLASTSCHRIFT_INFO', payment.id,
                    `Die Rücklastschrift über ${round2(gross)} € (${monthLabel(paymentKey)}) ändert unter Soll-Besteuerung nichts an der Steuer – die Forderung besteht weiter. Bei endgültigem Ausfall bitte Forderungsausfall (§ 17 UStG) erfassen.`,
                    {deepLink: c.source.deepLink}));
            }
            continue;
        }

        if (treatment === 'ALREADY_SOLL_TAXED') {
            // ALREADY_SOLL_TAXED setzt einen Leistungsmonat voraus (ohne Buchung gilt der Zuflussmonat als Leistung)
            const leistungLabel = monthLabel(lm ? lm.monthKey : paymentKey);
            const line = buildLine({
                rule: 'AUSGESCHLOSSEN', source: c.source, monthKey: paymentKey, monthSource: 'PAYMENT_DATE',
                category: c.category, rate: 0, gross,
                label: `${baseLabel} – bereits soll-versteuert (Leistung ${leistungLabel})`, issueIDs: issueIDsOf(issues),
            });
            out.lines.push(line);
            out.issues.push(...linkIssues(issues, line.id));
            continue;
        }

        if (treatment === 'KU_ALTUMSATZ') {
            if (c.category !== null && !isTaxableCategory(c.category)) {
                continue; // steuerfreie Kategorie: Nachweis läuft über die Soll-Seite (Kz 48)
            }
            const line = buildLine({
                rule: 'KU_ALTUMSATZ', source: c.source, monthKey: paymentKey, monthSource: 'PAYMENT_DATE',
                category: c.category, rate: 0, gross,
                label: `${baseLabel} – Leistung im Kleinunternehmer-/steuerfreien Jahr`, issueIDs: issueIDsOf(issues),
            });
            out.lines.push(line);
            out.issues.push(...linkIssues(issues, line.id));
            continue;
        }

        if (treatment === 'PROFILE_YEAR_MISMATCH') {
            issues.push(createIssue('PROFILE_YEAR_MISMATCH', payment.id,
                `Die Leistung ${lm ? monthLabel(lm.monthKey) : ''} fiel in ein regelbesteuertes Jahr (Ist-Besteuerung), der Zufluss ${monthLabel(paymentKey)} in ein Jahr ohne Regelbesteuerung. Bitte Steuerprofil prüfen – so entstünde eine Nichtbesteuerung.`,
                {deepLink: setupDeepLink(ctx.landlordID)}));
        }

        const line = buildLine({
            rule: payment.isInternal ? 'VERRECHNUNG' : 'IST', source: c.source, monthKey: paymentKey, monthSource: 'PAYMENT_DATE',
            category: c.category, rate: c.rate, gross, label: baseLabel, issueIDs: issueIDsOf(issues),
        });
        out.lines.push(line);
        out.issues.push(...linkIssues(issues, line.id));
    }

    // INFO: offene IST-Sollstellungen ohne Zufluss
    for (const booking of bookings) {
        const c = classifyBooking(ctx, booking);
        const lm = resolveLeistungsmonat(booking);
        if (c.excluded || !lm || classifyBookingForSoll(ctx.profileAt, monthStart(lm.monthKey)) !== 'VIA_PAYMENTS') {
            continue;
        }
        const open = openGross(booking);
        if (open > 0.005) {
            out.issues.push(createIssue('OFFENE_SOLLSTELLUNG_IST', booking.id,
                `${artLabel(booking.art)} ${monthLabel(lm.monthKey)}: ${open} € sind noch nicht zugeflossen. Unter Ist-Besteuerung entsteht die Steuer erst mit der Zahlung.`,
                {deepLink: c.source.deepLink}));
        }
    }

    return out;
};
